import { useQuery } from '@tanstack/react-query';
import { apiFetch, ApiError } from './client.ts';
import { useSession } from '../auth/session.tsx';
import { queryDefaults } from './queries.ts';
import { fileName } from '../documentName.ts';

/**
 * One of a client's Clio copies as the API shows it (132; 20 October): its name and folders as Clio has them, the
 * matter's own root folder dropped by the API, and the type, date, kind and size. The file's own name is never the
 * name a download is given ("Lastname, Firstname - Type - Date", set by the API).
 */
export interface ClientDocument {
  id: string;
  name: string;
  folder: string[];
  /** Cut at the client's own matter folder; only these join the merged view (143). */
  anchored: boolean;
  /** How the preview shows it: a PDF or a picture in full, a video in the player; null says what it is, with Download. */
  viewer: 'pdf' | 'image' | 'video' | null;
  type: { code: string; label: string; group: string | null; from: 'filename' | 'folder' | 'none' };
  date: string | null;
  date_from: 'filename' | 'clio' | 'copy' | null;
  kind: string;
  bytes: number | null;
  openable: boolean;
  state: string | null;
}

export function useClientDocuments(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'documents'],
    queryFn: () => apiFetch<{ documents: ClientDocument[]; counts: { total: number; openable: number } }>(
      `/clients/${clientId}/documents`, getToken),
    ...queryDefaults,
  });
}

/**
 * One document's bytes, through Procura: fetched with the user's own token (the server audits the look, then streams
 * the file with its own identity), with the generated name from Content-Disposition. No link to storage.
 */
export async function fetchDocument(getToken: () => Promise<string>, clientId: string, docId: string): Promise<{ blob: Blob; name: string }> {
  const res = await fetch(`/api/clients/${clientId}/documents/${docId}/file`, {
    headers: { authorization: `Bearer ${await getToken()}` },
  });
  if (!res.ok) {
    let message = `The document could not be opened (${res.status}).`;
    let hint: string | undefined;
    try { const b = (await res.json()) as { error?: string; hint?: string }; message = b.error ?? message; hint = b.hint; } catch { /* not JSON */ }
    throw new ApiError(res.status, message, hint);
  }
  return { blob: await res.blob(), name: fileName(res.headers.get('content-disposition')) };
}

/**
 * Saves one document under the generated name. The only way a file leaves the page: a document is never put in a tab
 * of its own, because a browser that cannot show a type saves a blob: address under its internal id (the .docx bug,
 * 20 October), whatever the server named it.
 */
export async function downloadClientDocument(getToken: () => Promise<string>, clientId: string, docId: string): Promise<void> {
  const { blob, name } = await fetchDocument(getToken, clientId, docId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
